import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CreditCard, Phone, Mail } from 'lucide-react';
import type { BookingOrgProfile, EstimateResponse } from '../../lib/bookingApi';

interface LocationState {
  estimate: EstimateResponse;
  bookingToken: string;
  orgProfile: BookingOrgProfile;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function BookingDepositPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  if (!state?.bookingToken || !state?.orgProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mist px-4">
        <div className="bg-white rounded-lg shadow-brand-md p-8 text-center max-w-sm">
          <h1 className="text-xl font-semibold text-midnight mb-2">Session Expired</h1>
          <p className="text-slate text-body mb-4">
            Please go back and fill out the booking form again.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/${slug}`)}
            className="text-plum font-medium hover:underline"
          >
            Return to booking form
          </button>
        </div>
      </div>
    );
  }

  const { estimate, orgProfile } = state;
  const hasRange = estimate?.rangeLow != null && estimate?.rangeHigh != null;
  const brandPrimary = orgProfile.bookingConfig?.brandPrimaryColor ?? '#6B2D8B';
  const brandText = orgProfile.bookingConfig?.brandTitleTextColor ?? '#FFFFFF';

  return (
    <div className="min-h-screen bg-mist">
      {/* Header */}
      <header
        className="border-b border-stone px-4 py-4"
        style={{ backgroundColor: brandPrimary, color: brandText }}
      >
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-white/10 transition-colors shrink-0"
            aria-label="Go back"
          >
            <ArrowLeft size={20} style={{ color: brandText }} />
          </button>
          {orgProfile.logoBase64 ? (
            <img
              src={orgProfile.logoBase64}
              alt={orgProfile.name}
              className="w-8 h-8 rounded-md object-contain shrink-0"
            />
          ) : (
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center font-bold shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: brandText }}
            >
              {orgProfile.name.charAt(0)}
            </div>
          )}
          <h1 className="text-lg font-bold leading-tight truncate" style={{ color: brandText }}>{orgProfile.name}</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Heading */}
        <section className="bg-white rounded-lg shadow-brand-sm p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-plum/10 flex items-center justify-center mx-auto mb-4">
            <CreditCard size={28} className="text-plum" />
          </div>
          <h2 className="text-xl font-bold text-midnight mb-2">Secure Your Moving Date</h2>
          <p className="text-sm text-slate">
            A refundable deposit locks in your preferred moving date and ensures crew availability.
          </p>
        </section>

        {/* Estimate summary */}
        {estimate?.estimatedValue != null && (
          <section className="bg-white rounded-lg shadow-brand-sm p-5">
            <h3 className="text-sm font-semibold text-midnight mb-2">Estimated Cost</h3>
            <div className="text-2xl font-bold text-midnight">
              {hasRange
                ? `${formatCurrency(estimate.rangeLow!)} – ${formatCurrency(estimate.rangeHigh!)}`
                : `~${formatCurrency(estimate.estimatedValue)}`}
            </div>
          </section>
        )}

        {/* Payment placeholder */}
        <section className="bg-white rounded-lg shadow-brand-sm p-6 border-2 border-dashed border-stone">
          <div className="text-center py-4">
            <p className="text-sm text-slate mb-4">
              Payment processing coming soon.
            </p>
            <p className="text-body text-midnight font-medium">
              Please contact {orgProfile.name} directly to secure your moving date.
            </p>
          </div>
        </section>

        {/* Contact info */}
        <section className="bg-white rounded-lg shadow-brand-sm p-5">
          <h3 className="text-sm font-semibold text-midnight mb-3">Contact {orgProfile.name}</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-primary-theme">
              <Phone size={16} className="text-slate shrink-0" />
              <span>Contact us for availability</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-primary-theme">
              <Mail size={16} className="text-slate shrink-0" />
              <span>We&apos;ll send confirmation details to your email</span>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="text-center py-6">
        <span className="text-xs text-slate">Powered by <span className="font-semibold">IOMI Pro</span></span>
      </div>
    </div>
  );
}
